import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { FetchCampaignItemsParams, fetchCampaignItems } from 'api/campaigns';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useInfiniteScroll } from 'customHooks/useInfiniteScroll';
import { transformInfiniteQueryOptions } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignItem } from 'types/domain';
import { Meta, PaginatedLink } from 'types/pagination';
import {
    GivebutterInfiniteQueryParams,
    GivebutterUseInfiniteQueryOptions,
} from 'types/react-query';

const DEFAULT_PAGE_LIMIT = 20;

interface InfiniteCampaignItemsParams
    extends Omit<FetchCampaignItemsParams, 'is_bundle' | 'campaignId'> {
    isBundle?: boolean;
}

const useInfiniteCampaignItems = (
    params: GivebutterInfiniteQueryParams<InfiniteCampaignItemsParams>,
    options: GivebutterUseInfiniteQueryOptions<CampaignItem> = {},
) => {
    const { enabled = true, ...restOptions } = options;
    const { limit = DEFAULT_PAGE_LIMIT, isBundle = false, ...rest } = params;
    const nextParams = { limit, is_bundle: isBundle, ...rest };
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id ?? 0;

    const infiniteQueryData = useSuspenseInfiniteQuery({
        initialPageParam: 1,
        queryKey: campaignQueryKeys.itemsFiltered(campaignId, nextParams),
        queryFn: ({ pageParam }) => {
            if (enabled) {
                return fetchCampaignItems({
                    campaignId,
                    page: pageParam,
                    is_auction: false,
                    ...nextParams,
                });
            }

            return {
                data: [],
                meta: {} as Meta,
                links: {} as PaginatedLink,
            };
        },
        ...transformInfiniteQueryOptions<CampaignItem>(),
        ...restOptions,
    });

    const ref = useInfiniteScroll(infiniteQueryData);

    return { ...infiniteQueryData, ref };
};

export { useInfiniteCampaignItems };
